import Ammo from "ammojs-typed";
class RigidBody {
    margin;
    transform;
    motionState;
    shape;
    inertia;
    body;
    /**
     * Rigid body.
     * @param config the body's physics properties configuration.
     * @param position the body's position.
     * @param rotation the body's rotation.
     * @param shapeController class responsible for creating a physics shape.
     * @param meshController the entity's mesh.
     */
    constructor(config, position, rotation, shapeController, meshController) {
        this.margin = 0.05;
        this.transform = this.createTransform(position, rotation);
        this.motionState = new Ammo.btDefaultMotionState(this.transform);
        this.shape = shapeController.createShape(meshController.getGeometry, this.margin);
        this.inertia = this.calculateInertia(config);
        this.body = this.createBody(config);
    }
    /**
     * Convert a ThreeJS Vector3 into an AmmoJS Vector3.
     * @param position the ThreeJS position Vector3.
     * @returns the converted vector.
     */
    convertPosition(position) {
        return new Ammo.btVector3(position.x, position.y, position.z);
    }
    /**
     * Convert a ThreeJS Quaternion into an AmmoJS Quaternion.
     * @param rotation the ThreeJS rotation Quaternion.
     * @returns the converted quaternion.
     */
    convertRotation(rotation) {
        return new Ammo.btQuaternion(rotation.x, rotation.y, rotation.z, rotation.w);
    }
    /**
     * Create and configure a transform for the rigid body.
     * @param position the initial position.
     * @param rotation the initial rotation.
     * @returns the created transform object.
     * @private
     */
    createTransform(position, rotation) {
        const transform = new Ammo.btTransform();
        transform.setIdentity();
        transform.setOrigin(this.convertPosition(position));
        transform.setRotation(this.convertRotation(rotation));
        return transform;
    }
    /**
     * Calculate the rigid body's inertia.
     * @param config the body's configuration object.
     * @returns the calculated inertia.
     * @private
     */
    calculateInertia(config) {
        const inertia = new Ammo.btVector3(0, 0, 0);
        if (config.MASS > 0)
            this.shape.calculateLocalInertia(config.MASS, inertia);
        return inertia;
    }
    /**
     * Create and configure the rigid body.
     * @param config the body's configuration object.
     * @returns the created rigid body.
     * @private
     */
    createBody(config) {
        const info = new Ammo.btRigidBodyConstructionInfo(config.MASS, this.getMotionState, this.getShape, this.getInertia);
        const body = new Ammo.btRigidBody(info);
        body.setRestitution(config.RESTITUTION);
        if (config.FRICTION)
            body.setFriction(config.FRICTION);
        if (config.ROLLING_FRICTION)
            body.setRollingFriction(config.ROLLING_FRICTION);
        body.setActivationState(config.ACTIVATION_STATE);
        body.setCollisionFlags(config.COLLISION_FLAGS);
        return body;
    }
    get getMotionState() {
        return this.motionState;
    }
    get getInertia() {
        return this.inertia;
    }
    get getTransform() {
        return this.transform;
    }
    get getShape() {
        return this.shape;
    }
    get getBody() {
        return this.body;
    }
    get getMargin() {
        return this.margin;
    }
}
export { RigidBody };
